import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useApi, errMsg, fmtDate, mediaUrl } from "@/lib/api";
import { useChannels } from "@/components/ChannelGrid";
import { Empty, MiniPlay, StatusBadge, fieldCls } from "@/components/dashboard/Shared";

function QueueItem({ t, channels, reload }) {
  const api = useApi();
  const [comment, setComment] = useState("");
  const decide = async (approve) => {
    try {
      await api("post", `/curator/radio/${t.id}`, { approve, comment });
      toast.success(approve ? "Canción aprobada: ya suena en la radio" : "Canción rechazada");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`queue-item-${t.id}`} className="rounded-xl border border-white/10 bg-[#12121A] p-5">
      <div className="flex items-center gap-4">
        <MiniPlay track={t} testId={`queue-play-${t.id}`} />
        {t.cover_url && <img src={mediaUrl(t.cover_url)} alt="" className="h-12 w-12 rounded object-cover" />}
        <div className="min-w-0 flex-1">
          <p className="truncate font-heading font-bold text-white">{t.title} {t.priority > 0 && <span data-testid={`queue-pro-${t.id}`} className="ml-1 rounded-full bg-[#FF4500] px-2 py-0.5 font-mono text-[10px] text-white">PRO</span>}</p>
          <p className="text-xs text-slate-400">{t.artist_name} · canal <span className="text-[#FF7A45]">{channels?.find((c) => c.slug === t.channel_slug)?.name || t.channel_slug}</span></p>
        </div>
      </div>
      {t.lyrics && <details className="mt-3 text-sm text-slate-400"><summary className="cursor-pointer">Ver letra</summary><pre className="mt-2 whitespace-pre-wrap font-body">{t.lyrics}</pre></details>}
      <Textarea data-testid={`queue-comment-${t.id}`} placeholder="Comentario para el artista (obligatorio si rechazas)" value={comment} onChange={(e) => setComment(e.target.value)} className={`mt-4 ${fieldCls}`} />
      <div className="mt-3 flex gap-2">
        <Button size="sm" data-testid={`queue-approve-${t.id}`} onClick={() => decide(true)} className="rounded-full bg-emerald-500 text-white hover:bg-emerald-600">Aprobar</Button>
        <Button size="sm" data-testid={`queue-reject-${t.id}`} onClick={() => decide(false)} variant="outline" className="rounded-full border-red-400/40 bg-transparent text-red-300 hover:bg-red-500/10">Rechazar</Button>
      </div>
    </div>
  );
}

export function RadioQueue() {
  const api = useApi();
  const channels = useChannels();
  const [items, setItems] = useState(null);
  const reload = () => api("get", "/curator/radio-queue").then(setItems).catch(() => setItems([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);
  if (!items) return null;
  if (!items.length) return <Empty>No hay canciones esperando aprobación.</Empty>;
  return <div data-testid="radio-queue" className="grid gap-4 lg:grid-cols-2">{items.map((t) => <QueueItem key={t.id} t={t} channels={channels} reload={reload} />)}</div>;
}

function InboxItem({ s, reload }) {
  const api = useApi();
  const [feedback, setFeedback] = useState("");
  const respond = async (aired) => {
    try {
      await api("post", `/curator/submissions/${s.id}/respond`, { aired, feedback });
      toast.success("Respuesta enviada al artista");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`inbox-item-${s.id}`} className="rounded-xl border border-white/10 bg-[#12121A] p-5">
      <div className="flex flex-wrap items-center gap-4">
        <MiniPlay track={s.track} testId={`inbox-play-${s.id}`} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-heading font-bold text-white">{s.track?.title} <span className="font-body font-normal text-slate-500">· {s.track?.artist_name}</span></p>
          <p className="font-mono text-[11px] text-slate-500">{s.media_name} · {s.credits} créditos · vence {fmtDate(s.deadline)}</p>
        </div>
        <StatusBadge status={s.status} testId={`inbox-status-${s.id}`} />
      </div>
      {s.status === "pending" ? (
        <>
          <Textarea data-testid={`inbox-feedback-${s.id}`} placeholder="Tu opinión para el artista" value={feedback} onChange={(e) => setFeedback(e.target.value)} className={`mt-4 ${fieldCls}`} />
          <div className="mt-3 flex gap-2">
            <Button size="sm" data-testid={`inbox-aired-${s.id}`} disabled={feedback.trim().length < 3} onClick={() => respond(true)} className="rounded-full bg-emerald-500 text-white hover:bg-emerald-600">Emitida</Button>
            <Button size="sm" data-testid={`inbox-not-aired-${s.id}`} disabled={feedback.trim().length < 3} onClick={() => respond(false)} variant="outline" className="rounded-full border-white/20 bg-transparent text-slate-200 hover:bg-white/10">No emitida</Button>
          </div>
        </>
      ) : s.feedback && <p className="mt-3 border-l-2 border-white/20 pl-3 text-sm text-slate-400">“{s.feedback}”</p>}
    </div>
  );
}

export function Inbox({ onLoad }) {
  const api = useApi();
  const [items, setItems] = useState(null);
  const reload = () => api("get", "/curator/inbox").then((d) => { setItems(d); onLoad?.(); }).catch(() => setItems([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);
  if (!items) return null;
  if (!items.length) return <Empty>Todavía no recibiste envíos.</Empty>;
  return <div data-testid="curator-inbox" className="space-y-4">{items.map((s) => <InboxItem key={s.id} s={s} reload={reload} />)}</div>;
}

export function MyMedia() {
  const api = useApi();
  const [m, setM] = useState(null);
  useEffect(() => { api("get", "/curator/media").then((d) => setM(d[0] ? { ...d[0], genres: d[0].genres.join(", ") } : false)).catch(() => setM(false)); }, [api]);
  if (m === null) return null;
  if (!m) return <Empty>No tienes un medio registrado.</Empty>;
  const save = async () => {
    try {
      const { id, owner_id, hidden, created_at, ...rest } = m;
      await api("put", `/curator/media/${id}`, { ...rest, genres: m.genres.split(",").map((g) => g.trim()).filter(Boolean), cost_credits: Number(m.cost_credits) });
      toast.success("Medio actualizado");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const set = (k) => (e) => setM({ ...m, [k]: e.target.value });
  return (
    <div data-testid="my-media-form" className="max-w-2xl space-y-4 rounded-xl border border-white/10 bg-[#12121A] p-6">
      <Input data-testid="media-edit-name" value={m.name} onChange={set("name")} className={fieldCls} placeholder="Nombre" />
      <Textarea data-testid="media-edit-desc" value={m.description} onChange={set("description")} className={fieldCls} placeholder="Descripción" />
      <Input data-testid="media-edit-genres" value={m.genres} onChange={set("genres")} className={fieldCls} placeholder="Géneros" />
      <div className="grid gap-4 sm:grid-cols-3">
        <Input data-testid="media-edit-country" value={m.country} onChange={set("country")} className={fieldCls} placeholder="País" />
        <Input data-testid="media-edit-format" value={m.format} onChange={set("format")} className={fieldCls} placeholder="Formato" />
        <Input data-testid="media-edit-cost" type="number" min={1} max={3} value={m.cost_credits} onChange={set("cost_credits")} className={fieldCls} />
      </div>
      <Input data-testid="media-edit-logo" value={m.logo_url || ""} onChange={set("logo_url")} className={fieldCls} placeholder="URL de imagen / logo" />
      <Button data-testid="media-edit-save" onClick={save} className="rounded-full bg-[#FF4500] text-white hover:bg-[#ff5a1f]">Guardar</Button>
    </div>
  );
}
